import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import type { FlexibleCancellationDateChangeAllowance } from './flexibleCancellationPolicyTimelineUtils'

interface Props {
  dateChangeAllowance: FlexibleCancellationDateChangeAllowance
  /** Formatted first day of the date-change window. */
  startDate: string
  /** Formatted last day of the date-change window. */
  endDate: string
}

/**
 * "Date changes" section of a dated cancellation policy timeline, shared by the flexible and
 * Fully refundable timelines so the allowance copy lives in one place.
 */
function CancellationPolicyDateChanges(props: Props) {
  const { dateChangeAllowance, startDate, endDate } = props
  const intl = useIntl()
  const dateChangeWindow = { startDate, endDate }

  return (
    <div>
      <BodyText variant="medium" weight="bold">
        {intl.formatMessage({
          id: 'zOgZxgWqO_0Kba00Iaqk',
          defaultMessage: 'Date changes',
          description: 'Section heading for date changes in the date selection or calendar picker',
        })}
      </BodyText>
      <BodyText variant="medium">
        {dateChangeAllowance.kind === 'unlimited' && intl.formatMessage({
          id: 'eTdPkOsXQYv_WWnmOfTl',
          defaultMessage: 'Unlimited changes permitted between {startDate} and {endDate}. Subject to availability and applicable surcharges (peak periods or property-specific).',
          description: 'Date change policy text with date range when the offer allows unlimited date changes',
        }, dateChangeWindow)}
        {dateChangeAllowance.kind === 'limited' && dateChangeAllowance.count === 1 && intl.formatMessage({
          id: '7faDfrrNU0w8A0OdSY9Z',
          defaultMessage: 'One change permitted between {startDate} and {endDate}. Subject to availability and applicable surcharges (peak periods or property-specific).',
          description: 'Date change policy text with date range in the date selection or calendar picker',
        }, dateChangeWindow)}
        {dateChangeAllowance.kind === 'limited' && dateChangeAllowance.count > 1 && intl.formatMessage({
          id: 'Jd4m8kbiUW3V-7OGOMq8',
          defaultMessage: '{count, plural, one {One change} other {# changes}} permitted between {startDate} and {endDate}. Subject to availability and applicable surcharges (peak periods or property-specific).',
          description: 'Date change policy text with date range when the offer allows a limited number of date changes',
        }, { ...dateChangeWindow, count: dateChangeAllowance.count })}
        {dateChangeAllowance.kind === 'none' && intl.formatMessage({
          id: 'q5IDmM7KYiN5jzfti-vk',
          defaultMessage: 'Date changes are not permitted for this offer.',
          description: 'Date change policy text in the cancellation policy modal when the offer does not allow date changes',
        })}
        {dateChangeAllowance.kind === 'unknown' && intl.formatMessage({
          id: 'swrejTr_Tm7rw0BpmU0T',
          defaultMessage: 'Changes may be permitted between {startDate} and {endDate}, as per the offer terms and conditions. Subject to availability and applicable surcharges (peak periods or property-specific).',
          description: 'Date change policy text with date range when the number of date changes allowed for the offer is unknown',
        }, dateChangeWindow)}
      </BodyText>
    </div>
  )
}

export default CancellationPolicyDateChanges
